/**
 * PATH: packages/job-hub/src/jobs/qa/QaChecklistSectionBody.tsx
 * FILE_ID: JOBHUB-QA-SECTION-BODY-001
 * PURPOSE: One origin block inside a Build checklist: header, rows, Add row.
 */
import { Fragment } from 'react'
import { Library, Loader2, Plus } from 'lucide-react'
import type { ContextMenuItem } from '@quotech/ui'
import { QaChecklistRow } from './QaChecklistRow'
import { QaInlineMenuButtons } from './qaInlineMenuButtons'
import type { QaItemEdit, QaItemInstance } from './qaApi'
import type { QaRowNode } from './checklistTable'

export function QaChecklistSectionBody({
  title,
  origin,
  nodes,
  disabled,
  busy,
  onEditCell,
  onContextMenu,
  itemSelection,
  checklistId,
  onAdd,
  addBusy,
  areaNames = [],
  checklistNames = [],
  showHeader = true,
  itemIds,
  actionRowId = null,
  actionItems,
  onActionDone,
  onQuickDelete,
  onMoveUp,
  onMoveDown,
  onInsertFromLibrary,
}: {
  title: string
  origin: 'standard' | 'quote'
  nodes: QaRowNode<QaItemInstance>[]
  disabled: boolean
  busy: string | null
  onEditCell: (item: QaItemInstance, fields: QaItemEdit) => void
  onContextMenu: (node: QaRowNode<QaItemInstance>, event: React.MouseEvent | { clientX: number; clientY: number }) => void
  itemSelection: {
    isSelected: (id: string, checklistId?: string, areaId?: string) => boolean
    onToggle: (id: string, checklistId?: string, areaId?: string) => void
  }
  checklistId?: string
  onAdd: () => void
  addBusy: boolean
  areaNames?: string[]
  checklistNames?: string[]
  showHeader?: boolean
  itemIds?: string[]
  actionRowId?: string | null
  actionItems?: ContextMenuItem[]
  onActionDone?: () => void
  onQuickDelete?: (item: QaItemInstance) => void
  onMoveUp?: (item: QaItemInstance) => void
  onMoveDown?: (item: QaItemInstance) => void
  onInsertFromLibrary?: () => void
}) {
  // Reused from: packages/job-hub/src/jobs/tabs/job-qa/useQaSelection.ts (toggleCheck) through itemSelection.onToggle per row.
  const all = Boolean(itemIds?.length) && itemIds!.every((id) => itemSelection.isSelected(id, checklistId))
  const toggleAll = () => {
    const target = !all
    for (const id of itemIds ?? []) {
      if (itemSelection.isSelected(id, checklistId) !== target) itemSelection.onToggle(id, checklistId)
    }
  }
  return (
    <>
      {showHeader && (
        <tr data-testid={`qa-origin-section-${origin}`}>
          <td colSpan={6} className="bg-muted/40 px-3 py-1.5 text-[11px] font-semibold text-foreground">
            {itemIds?.length ? (
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 shrink-0 rounded border-border"
                  aria-label={`Select all ${title}`}
                  data-testid={`qa-origin-select-${origin}`}
                  checked={all}
                  disabled={disabled}
                  onChange={toggleAll}
                />
                {title}
              </label>
            ) : title}
          </td>
        </tr>
      )}
      {nodes.map((node) => (
        <Fragment key={node.item.id}>
          <QaChecklistRow
            node={node}
            disabled={disabled}
            rowBusy={busy === `cell-${node.item.id}` || busy === `del-${node.item.id}`}
            onEditCell={onEditCell}
            onContextMenu={(event) => onContextMenu(node, event)}
            selected={itemSelection.isSelected(node.item.id, checklistId)}
            onToggleSelected={() => itemSelection.onToggle(node.item.id, checklistId)}
            areaNames={areaNames}
            checklistNames={checklistNames}
            onQuickDelete={onQuickDelete ? () => onQuickDelete(node.item) : undefined}
            onMoveUp={onMoveUp ? () => onMoveUp(node.item) : undefined}
            onMoveDown={onMoveDown ? () => onMoveDown(node.item) : undefined}
          />
          {actionRowId === node.item.id && actionItems ? (
            <tr>
              <td colSpan={6}>
                <QaInlineMenuButtons items={actionItems} onDone={onActionDone ?? (() => undefined)} />
              </td>
            </tr>
          ) : null}
        </Fragment>
      ))}
      <tr>
        <td colSpan={6} className="px-3 py-1.5">
          <button
            type="button"
            data-testid={`qa-add-row-${origin}`}
            onClick={onAdd}
            disabled={disabled || addBusy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:border-primary hover:text-primary disabled:opacity-50"
          >
            {addBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Plus className="h-3.5 w-3.5" aria-hidden />}
            Add row
          </button>
          {onInsertFromLibrary ? (
            <button
              type="button"
              data-testid={`qa-insert-library-${origin}`}
              onClick={onInsertFromLibrary}
              disabled={disabled}
              className="ml-2 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:border-primary hover:text-primary disabled:opacity-50"
            >
              <Library className="h-3.5 w-3.5" aria-hidden />
              Insert from library
            </button>
          ) : null}
        </td>
      </tr>
    </>
  )
}
